// Bantu deteksi "semirip mungkin realtime" di atas HF Space (hasil /analyze datang ~1x per detik, video 25-30 fps).
// Fungsi murni, tanpa DOM, supaya bisa diuji dengan node (lihat tests/realtimeScan.test.mjs).
//
// Pembagian kerja:
//   - pengirim frame berjalan "tanpa antre": frame berikutnya diambil SETELAH balasan kembali (bukan setInterval),
//     jadi tidak pernah ada lebih dari satu permintaan terbang per kamera dan frame yang dikirim selalu yang terbaru.
//   - kotak digambar di rAF dari hasil terakhir + ekstrapolasi gerak (kecepatan dari dua hasil terakhir).
//   - kendaraan yang platnya sudah terbaca tidak di-OCR ulang (server melewati kotak yang dikirim di `known_vehicles`).

export const iou = (a, b) => {
  const ix = Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0]));
  const iy = Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
  const inter = ix * iy;
  const ua = (a[2] - a[0]) * (a[3] - a[1]) + (b[2] - b[0]) * (b[3] - b[1]) - inter;
  return ua > 0 ? inter / ua : 0;
};

// Skala semua bbox dari ruang frame terkirim (mis. 1280 lebar) ke ruang sumber (mis. 1920).
export function scaleRawBoxes(raw, k) {
  if (!raw || k === 1) return raw;
  const s = (b) => (Array.isArray(b) && b.length >= 4 ? b.map((v) => Math.round(v * k)) : b);
  return {
    ...raw,
    objects: (raw.objects || []).map((o) => ({ ...o, bbox: s(o.bbox) })),
    faces: (raw.faces || []).map((f) => ({ ...f, bbox: s(f.bbox) })),
    plates: (raw.plates || []).map((p) => ({
      ...p, from_bbox: s(p.from_bbox), teks_bbox: s(p.teks_bbox), bbox: s(p.bbox),
    })),
  };
}

// Pelacak IoU sederhana. Tiap item punya id stabil, bbox terakhir, bbox sebelumnya, dan waktunya.
// Cukup untuk kamera tetap dengan gerak lambat; bukan pengganti ByteTrack.
export function createTracker({ minIou = 0.3, maxAgeMs = 3000 } = {}) {
  let tracks = [];
  let nextId = 1;
  return {
    update(bboxes, now) {
      const used = new Set();
      const out = bboxes.map((bb) => {
        let best = null, bestIou = minIou;
        for (const t of tracks) {
          if (used.has(t.id)) continue;
          const v = iou(t.bbox, bb);
          if (v > bestIou) { best = t; bestIou = v; }
        }
        if (best) {
          used.add(best.id);
          best.prev = best.bbox; best.tPrev = best.t;
          best.bbox = bb; best.t = now;
          return best;
        }
        const t = { id: nextId++, bbox: bb, prev: null, tPrev: null, t: now, data: {} };
        tracks.push(t); used.add(t.id);
        return t;
      });
      tracks = tracks.filter((t) => now - t.t <= maxAgeMs);
      return out;
    },
    snapshot() { return tracks; },
  };
}

// Posisi yang diperkirakan pada waktu `now`. Faktor ekstrapolasi dibatasi supaya kotak tidak melayang
// jauh bila balasan terlambat.
export function extrapolate(track, now, { maxFactor = 1.5 } = {}) {
  if (!track.prev || track.tPrev == null || track.t <= track.tPrev) return track.bbox;
  const f = Math.min(maxFactor, (now - track.t) / (track.t - track.tPrev));
  if (!(f > 0)) return track.bbox;
  return track.bbox.map((v, i) => Math.round(v + (v - track.prev[i]) * f));
}
